import { useEffect, useId, useState } from 'react'
import { Save } from 'lucide-react'
import { decimalInputToMinor, minorToDecimalInput } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AreaGroup } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { createAreaGroup, updateAreaGroup } from './api'
import styles from './AreaPricingPage.module.css'

interface GroupFormDialogProps {
  open: boolean
  /** The group being edited, or null to create one. */
  group: AreaGroup | null
  /** The group that is primary now, if any — named when this one would take its place. */
  currentPrimary: AreaGroup | null
  onClose: () => void
}

const MAX_SURCHARGE_MINOR = 10_000_000 // 100,000 EGP, the server's ceiling

/**
 * Creates or edits an area group: its name, whether it is the primary group,
 * and otherwise what it costs to reach the primary group from there.
 *
 * The surcharge is entered before VAT, like every catalogue price — the app
 * adds VAT to the package price with the surcharge already in it.
 */
export function GroupFormDialog({ open, group, currentPrimary, onClose }: GroupFormDialogProps) {
  const [name, setName] = useState('')
  const [surcharge, setSurcharge] = useState('')
  const [primary, setPrimary] = useState(false)
  const [touched, setTouched] = useState(false)
  const formId = useId()

  useEffect(() => {
    if (!open) return
    setName(group?.name ?? '')
    setSurcharge(group && !group.primary ? minorToDecimalInput(group.surchargeMinor) : '')
    setPrimary(group?.primary ?? false)
    setTouched(false)
  }, [open, group])

  const surchargeMinor = surcharge.trim() === '' ? 0 : decimalInputToMinor(surcharge)
  const nameError = touched && name.trim().length === 0 ? 'Give the group a name.' : undefined
  const surchargeError =
    primary || surcharge.trim() === ''
      ? undefined
      : surchargeMinor === null
        ? 'Enter an amount like 80 or 80.50.'
        : surchargeMinor > MAX_SURCHARGE_MINOR
          ? 'The surcharge must be 100,000 or less.'
          : undefined

  const takesPrimaryFrom = primary && currentPrimary && currentPrimary.id !== group?.id ? currentPrimary : null

  const mutation = useAdminMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        surcharge: primary ? 0 : (surchargeMinor ?? 0) / 100,
        primary,
      }
      return group ? updateAreaGroup(group.id, input) : createAreaGroup(input)
    },
    successTitle: group ? 'Group saved' : 'Group created',
    successDescription: (saved: AreaGroup) =>
      saved.primary
        ? `${saved.name} is the primary group: trips there add nothing, and every other group's surcharge is measured to it.`
        : `Each end of a Cairo trip in ${saved.name} now adds ${minorToDecimalInput(saved.surchargeMinor)} EGP before VAT.`,
    errorTitle: 'The group was not saved',
    invalidate: [queryKeys.areaPricing.all],
    onSuccess: () => onClose(),
  })

  const canSave = name.trim().length > 0 && !surchargeError && !mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={mutation.isPending}
      size="sm"
      title={group ? `Edit ${group.name}` : 'New area group'}
      description="A group of Cairo sub-areas that cost the same to reach — Downtown, East Cairo, West Cairo."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          {/* Submits the form below from the footer, so Enter in a field does the same. */}
          <Button
            type="submit"
            form={formId}
            variant="primary"
            iconLeft={<Save aria-hidden="true" />}
            loading={mutation.isPending}
            disabled={!canSave}
          >
            {group ? 'Save group' : 'Create group'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.panelBody}
        onSubmit={(event) => {
          event.preventDefault()
          setTouched(true)
          if (canSave) mutation.mutate(undefined)
        }}
      >
        <Field label="Name" required error={nameError}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={name}
              maxLength={100}
              placeholder="East Cairo"
              onChange={(event) => setName(event.target.value)}
              onBlur={() => setTouched(true)}
              disabled={mutation.isPending}
            />
          )}
        </Field>

        <label className={styles.checkRow}>
          <input
            type="checkbox"
            checked={primary}
            onChange={(event) => setPrimary(event.target.checked)}
            disabled={mutation.isPending}
          />
          <span>
            <strong>Primary group</strong>
            <span className={styles.checkHint}>
              The reference point: a trip end here adds nothing, and every other group&apos;s surcharge is
              the price of reaching this one.
              {takesPrimaryFrom ? ` ${takesPrimaryFrom.name} stops being primary.` : ''}
            </span>
          </span>
        </label>

        <Field
          label="Surcharge (EGP, before VAT)"
          error={surchargeError}
          hint={
            primary
              ? 'The primary group never adds anything.'
              : 'Added once for each end of a Cairo trip in this group: East → Downtown pays it once, East → East twice.'
          }
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              inputMode="decimal"
              value={primary ? '0.00' : surcharge}
              placeholder="0.00"
              onChange={(event) => setSurcharge(event.target.value)}
              disabled={primary || mutation.isPending}
            />
          )}
        </Field>
      </form>
    </Dialog>
  )
}
